import { useTranslation } from "@outschool/localization";
import { CurrentUser } from "@outschool/ui-auth";
import React from "react";

import { ACTION_TYPES, chatReducer } from "./chatReducer";
import { ErrorMessage } from "./components/ErrorMessage";
import { Message as MessageComponent } from "./components/Message";
import { ConciergeChatMessage } from "./conciergeChat/ConciergeChatMessage";
import { MESSAGE_ID, Message, MessageIdType, Sender } from "./consts";

export type StateType = {
  currentMessageId: MessageIdType;
  conversation: Message[];
  selectedLearner?: SelectedLearner;
};
export type SelectedLearner = NonNullable<CurrentUser>["children"][0] | null;

export type ChatContextType = StateType & {
  sendConciergeRequestSubmit: (_: string, _learner?: SelectedLearner) => void;
  showErrorMessage: (_a: string, _b: string) => void;
  updateSelectedLearner: (_: SelectedLearner) => void;
  setIsModalOpen?: (_: boolean) => void;
  resetConciergeRequest: () => void;
};

const CONTEXT_DEFAULT_VALUE: ChatContextType = {
  currentMessageId: MESSAGE_ID.CONCIERGE_REQUEST,
  conversation: [],
  sendConciergeRequestSubmit: () => {},
  updateSelectedLearner: () => {},
  showErrorMessage: () => {},
  resetConciergeRequest: () => {}
};

export const INIT_STATE: StateType = {
  currentMessageId: MESSAGE_ID.CONCIERGE_REQUEST,
  conversation: []
};

const ChatContext = React.createContext<ChatContextType>(CONTEXT_DEFAULT_VALUE);

type ChatProviderProps = {
  setIsModalOpen?: (_: boolean) => void;
  children?: React.ReactNode;
  initState?: StateType;
};

export const ChatProvider: React.FC<
  React.PropsWithChildren<ChatProviderProps>
> = ({ setIsModalOpen, children, initState = INIT_STATE }) => {
  const { t } = useTranslation("client\\components\\chat");

  const [{ currentMessageId, conversation, selectedLearner }, dispatch] =
    React.useReducer(chatReducer, initState);

  const updateSelectedLearner = (learner: SelectedLearner) => {
    dispatch({
      type: ACTION_TYPES.UPDATE_LEARNER,
      payload: {
        learner
      }
    });
  };

  const sendConciergeRequestSubmit = (
    userMessage: string,
    selectedLearner: SelectedLearner
  ) => {
    dispatch({
      type: ACTION_TYPES.GO_TO_NEXT_MESSAGE,
      payload: {
        nextMessageId: MESSAGE_ID.CONCIERGE_REQUEST_RECEIVED,
        messages: [
          {
            sender: Sender.System,
            render: () => <ConciergeChatMessage />
          },
          {
            sender: Sender.User,
            render: () => (
              <MessageComponent
                text={
                  // append selected learner to message to indicate this context was added
                  (selectedLearner
                    ? `Learner: ${selectedLearner.name}\n`
                    : "") + userMessage
                }
                sender={Sender.User}
              />
            )
          },
          {
            sender: Sender.System,
            render: () => (
              <MessageComponent
                text={t`Thank you for sharing those details! We’ll review everything and send you personalized class recommendations within 2 business days via the email associated with your account. If you think of anything else, just respond to that email and let us know. We’re here to help!`}
                sender={Sender.System}
              />
            )
          }
        ]
      }
    });
  };

  const showErrorMessage = (userMessage: string, errorMessage: string) => {
    dispatch({
      type: ACTION_TYPES.GO_TO_NEXT_MESSAGE,
      payload: {
        nextMessageId: MESSAGE_ID.CONCIERGE_REQUEST,
        messages: [
          {
            sender: Sender.User,
            render: () => (
              <MessageComponent text={userMessage} sender={Sender.User} />
            )
          },
          {
            sender: Sender.System,
            render: () => <ErrorMessage text={errorMessage} />
          }
        ]
      }
    });
  };

  const resetConciergeRequest = () => {
    dispatch({
      type: ACTION_TYPES.RESET_CONCIERGE_REQUEST
    });
  };

  return (
    <ChatContext.Provider
      value={{
        currentMessageId,
        conversation,
        showErrorMessage,
        setIsModalOpen,
        sendConciergeRequestSubmit,
        updateSelectedLearner,
        selectedLearner,
        resetConciergeRequest
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => {
  return React.useContext(ChatContext);
};
